import React, { useEffect, useContext } from 'react';
import proptypes from 'prop-types';
import { useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { BuyAgainCarousel } from './BuyAgainCarousel';
import { SubscriptionsFallback } from './SubscriptionsFallback';
import { buyAgainModel } from './dataModel';
import {
  getReorderModel,
  getReorderCustType,
  getReorderCustID,
  getCustIsLoggedIn
} from '../utils/helpers';

const {
  oneOf, bool: boolType, number: numberType, shape: shapeType, string: stringType
} = proptypes;

const BuyAgain = ({
  brandTitleMaxLine,
  customEventObject,
  customerID,
  customerIsLoggedIn,
  customerType,
  excludeItems,
  filter,
  hideTitle,
  maxProducts,
  requestKey,
  showLoading,
  slidesPer,
  showViewAll,
  hideATC,
  noATCFulfillment,
  entryId,
  storefrontSvocId,
  slidesToShow,
}) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('buy-again.ready'); }, []);

  const { isLocalized, storeId, storeZip: zipCode } = useStore();
  const { channel } = useContext(ExperienceContext) || {};

  const {
    customerType: cType, isB2BCustomer, isLoggedIn, mcvisID, svocID
  } = useThdCustomer() || {};

  const custIsLoggedIn = getCustIsLoggedIn(customerIsLoggedIn, isLoggedIn);
  const custID = getReorderCustID(customerID, custIsLoggedIn, svocID, storefrontSvocId, mcvisID);
  const custType = getReorderCustType(customerType, cType);

  const apiName = 'reorder';
  const udsQueryOptions = {
    variables: {
      apiName,
      appId: channel === 'mobile' ? 'mobileweb' : 'desktop',
      cType: custType,
      exItems: excludeItems,
      dataSource: apiName,
      filter,
      key: requestKey,
      maxProducts,
      model: getReorderModel(isB2BCustomer),
      showPrice: true,
      storeId,
      // B2C non-auth: thda.m or mcvisidd, B2C and B2B auth: svocid
      tableName: 'reorder',
      userId: custID || '',
      zipCode,
    },
    skip: !isLocalized || storeId === '8119',
    ssr: false
  };

  const { data, loading, error } = useDataModel('uds', udsQueryOptions);
  const buyAgainError = error || (!data?.uds?.products?.length && !loading);

  return (
    <ImpressionProvider
      data={{
        id: entryId || '',
        name: buyAgainError ? 'BuyAgainFallback' : 'BuyAgain',
        component: 'BuyAgain',
        position: buyAgainError ? 1 : '',
        type: buyAgainError ? 'content' : 'product',
      }}
    >
      {buyAgainError
        ? <SubscriptionsFallback entryId={entryId} />
        : (
          <BuyAgainCarousel
            data={data?.uds}
            customEventObject={customEventObject}
            hideTitle={hideTitle}
            hideATC={hideATC}
            hideRating
            showSubscribeAndSave
            itemClass="fixed-item"
            loading={loading}
            brandTitleMaxLine={brandTitleMaxLine}
            showLoading={showLoading}
            storeId={storeId}
            zipCode={zipCode}
            slidesPer={slidesPer}
            scheme={apiName}
            noATCFulfillment={noATCFulfillment}
            hideControls={data?.uds?.products?.length <= 2}
            slidesToShow={slidesToShow}
            showViewAll={showViewAll}
            isB2BCustomer={isB2BCustomer}
          />
        )}
    </ImpressionProvider>
  );
};

BuyAgain.propTypes = {
  // product pod takes only these prop values for brandTitleMaxLine
  brandTitleMaxLine: oneOf([2, 3, 4, null]),
  customEventObject: shapeType({
    component: stringType,
    customEvent: stringType
  }),
  // svocid or mcvisid
  customerID: stringType,
  customerIsLoggedIn: stringType,
  customerType: stringType,
  excludeItems: stringType,
  filter: stringType,
  hideTitle: boolType,
  maxProducts: numberType,
  requestKey: stringType.isRequired,
  showLoading: boolType,
  slidesPer: numberType,
  hideATC: boolType,
  noATCFulfillment: boolType,
  entryId: stringType,
  storefrontSvocId: stringType,
  slidesToShow: shapeType({
    sm: numberType,
    md: numberType,
    lg: numberType,
  }),
  showViewAll: boolType
};

BuyAgain.defaultProps = {
  brandTitleMaxLine: 2,
  customEventObject: null,
  customerID: null,
  customerIsLoggedIn: null,
  customerType: null,
  excludeItems: '',
  filter: '',
  hideTitle: false,
  maxProducts: 20,
  showLoading: false,
  slidesPer: null,
  hideATC: false,
  noATCFulfillment: null,
  entryId: null,
  storefrontSvocId: null,
  slidesToShow: null,
  showViewAll: true
};

BuyAgain.displayName = 'BuyAgain';

BuyAgain.dataModel = buyAgainModel;

export { BuyAgain };
